"use client";

import { Core } from "@faire/design-tokens";
import { LoadingSpinner } from "@faire/web/slate-deprecated/LoadingSpinner";
import { legacyComponentCls } from "@faire/web/slate/utils/tracking";
import * as React from "react";
import styled from "styled-components";

import { Close } from "../../slate/icons/foundation/Close";
import { Search } from "../../slate/icons/foundation/Search";
import { Input } from "../../slate/Input";
import { Spacer } from "../../slate/spacing";

const Wrapper = styled.div`
  position: relative;
`;

const SearchWrapper = styled.div`
  pointer-events: none;
  display: flex;
  font-size: 16px;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate-deprecated/IconButton/IconButton#IconButton"
 */
const CloseWrapper = styled.button`
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export interface SearchInputProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  "data-test-id"?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  id?: string;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  title?: string;
  "data-hj-allow"?: boolean;
  maxLength?: number;
}

/**
 * @deprecated Use `@faire/slate/components/search` instead - since 2025-04-04
 *
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/component/input/web/search-input)
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  autoComplete,
  autoFocus,
  onChange,
  value,
  className,
  placeholder,
  onFocus,
  onBlur,
  onKeyUp,
  onKeyDown,
  id,
  disabled,
  label,
  loading,
  title,
  "data-hj-allow": dataHjAllow,
  maxLength,
  ...props
}) => {
  const ref = React.useRef<HTMLInputElement>(null);

  const onClear = () => {
    props.onClear();
    ref.current?.focus();
  };

  const renderSuffixNode = () => {
    if (loading) {
      return (
        <>
          <Spacer width="1x" />
          <LoadingSpinner size="small" color={Core.icon.subdued} />
        </>
      );
    } else if (value) {
      return (
        <>
          <Spacer width="1x" />
          <CloseWrapper onClick={onClear}>
            <Close color={Core.icon.subdued} />
          </CloseWrapper>
        </>
      );
    } else {
      return null;
    }
  };

  return (
    <Wrapper className={legacyComponentCls(className)}>
      <StyledInput
        disabled={disabled}
        id={id}
        data-test-id={props["data-test-id"]}
        onChange={onChange}
        value={value}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        prefixNode={
          <SearchWrapper>
            <Search color={Core.icon.default} />
          </SearchWrapper>
        }
        suffixNode={renderSuffixNode()}
        ref={ref}
        label={label}
        title={title}
        data-hj-allow={dataHjAllow}
        isButton={false}
        maxLength={maxLength}
      />
    </Wrapper>
  );
};

const StyledInput = styled(Input)`
  input {
    margin-top: -2px;
  }
`;
